import { ViewportScroller } from '@angular/common';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { ProductListFacade } from '@catalog/data-access';
import { Store } from '@ngrx/store';
import { CustomSearchBoxConfig, CustomSearchResults } from '@search/const';
import { SearchActions, SearchSelectors, StateWithSearch } from '@search/data-access';
import { ProductActions, RouterState, RoutingService, StateWithProduct } from '@spartacus/core';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { SearchCurrentQueryService } from './search-current-query.service';
import { SearchHistoryService } from './search-history.service';

@Injectable({
  providedIn: 'root',
})
export class CustomSearchBoxService {
  constructor(
    private routingService: RoutingService,
    private productListFacade: ProductListFacade,
    private searchStore: Store<StateWithSearch>,
    private productStore: Store<StateWithProduct>,
    private searchHistoryService: SearchHistoryService,
    private searchCurrentQueryService: SearchCurrentQueryService,
    private viewportScroller: ViewportScroller,
  ) {}

  search(query: string, config: CustomSearchBoxConfig): void {
    // нам необходимо чтобы подсказки грузились, в т.ч. поле поиска еще пустое
    // когда поле пустое подгружаются Часто ищут
    if (query.length === 0 || query.length >= config.minCharactersBeforeRequest) {
      this.searchStore.dispatch(new SearchActions.SearchSuggestionsLoad(query));
    }
  }

  getResults(): Observable<CustomSearchResults> {
    return this.getSuggestionResults().pipe(
      switchMap((suggestions: CustomSearchResults) => {
        return this.searchHistoryService.get(5, this.searchCurrentQueryService.query).pipe(
          map((searchHistory) => {
            return {
              keywordRedirectUrl: suggestions?.redirect,
              freeTextSearch: suggestions?.query,
              ...suggestions,
              // Если подсказка поискового запроса совпадает с историей запросов,
              // то такая подсказка поискового запроса скрывается.
              sts: searchHistory.length
                ? suggestions?.sts?.filter((i) => searchHistory.some((j) => j.text !== i.st))
                : suggestions?.sts || [],
              searchHistory,
            };
          }),
          map((result) => {
            // сколько подсказок каждого типа отображать для каких видов устройств
            const config = {
              taps: 6,
              sts: 10,
              categories: 3,
              searchHistory: 3,
            };

            return {
              ...result,
              sts: result?.sts?.slice(0, config.sts),
              taps: result?.taps?.slice(0, config.taps),
              searchHistory: result?.searchHistory?.slice(0, config.searchHistory),
            };
          }),
        );
      }),
    );
  }

  getCurrentQuery(): Observable<string> {
    return this.routingService
      .getRouterState()
      .pipe(
        map(
          (router: RouterState) =>
            this.normalizeFreeTextSearchQueryParam(router.state?.queryParams?.freeTextSearch) ||
            router.state?.params?.query ||
            '',
        ),
      );
  }

  getSuggestionLoading(): Observable<boolean> {
    return combineLatest([this.getCustomSuggestionLoading(), this.productListFacade.getLoadingSuggestions()]).pipe(
      map(
        ([searchBoxSuggestionLoading, productListSuggestionLoading]) =>
          searchBoxSuggestionLoading || productListSuggestionLoading,
      ),
      distinctUntilChanged(),
    );
  }

  /**
   * Событие отправляется при поиске
   */
  searchLaunch(query: string): void {
    this.searchStore.dispatch(new SearchActions.SearchLaunch(query));
  }

  clearResults(): void {
    this.productStore.dispatch(
      new ProductActions.ClearProductSearchResult({
        clearSearchboxResults: true,
      }),
    );
    this.searchStore.dispatch(new SearchActions.SearchSuggestionsLoadClear());
  }

  launchSearchPage(queryParams?: Params | null): void {
    this.viewportScroller.scrollToPosition([0, 0]);
    this.productListFacade.resetPageNumbers();
    this.routingService.go({ cxRoute: 'search' }, { queryParams });
  }

  getCustomSuggestionLoading(): Observable<boolean> {
    return this.searchStore.select(SearchSelectors.getSearchSuggestionsLoading);
  }

  getSuggestionResults(): Observable<CustomSearchResults> {
    return this.searchStore.select(SearchSelectors.getSearchSuggestionsValue);
  }

  // если нет параметра freeTextSearch содержит запятую, то он превращается в массив строк, поэтому превращаем обратно в строку
  // eslint-disable-next-line max-len
  // пример такой страницы /ekb/category/germetiki-5356?freeTextSearch=&freeTextSearch=герметик%20санитарный&sort=relevance&facets=allCategories:germetiki-5356,facet_attr-tip_cl-l3-germetiki:%25D0%25A1%25D0%25B0%25D0%25BD%25D0%25B8%25D1%2582%25D0%25B0%25D1%2580%25D0%25BD%25D1%258B%25D0%25B9
  private normalizeFreeTextSearchQueryParam(freeTextSearch?: string | string[]): string {
    return Array.isArray(freeTextSearch) ? freeTextSearch.join(' ') : freeTextSearch || '';
  }
}
